/** @typedef {[number, number]} Point */

import {
  DIMENSION_ARROW_TO_TEXT_GAP_PX,
  DIMENSION_SCREEN_ARROW_PX,
  DIMENSION_SCREEN_FONT_PX,
  DIMENSION_SCREEN_STROKE_PX,
  DIMENSION_WALL_TO_ARROW_GAP_PX,
} from './floorPlanDesignerConfig.js';

/**
 * @param {Point} point
 * @param {Array<Point>} polygon
 * @returns {boolean}
 */
function isPointInPolygon(point, polygon) {
  if (polygon.length < 3) return false;
  const [px, py] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i][0];
    const yi = polygon[i][1];
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect = (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Default test distance in screen pixels for room side detection. */
export const DIMENSION_SIDE_TEST_DISTANCE_PX = 20;

/**
 * @param {number} meters
 * @returns {string}
 */
export function formatMeters(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return '0 m';
  if (meters >= 10) return `${Math.round(meters)} m`;
  if (meters >= 1) return `${meters.toFixed(1)} m`;
  return `${meters.toFixed(2)} m`;
}

/**
 * @param {number} lengthPx
 * @param {number} metersPerPixel
 * @returns {string}
 */
export function formatLengthFromPixels(lengthPx, metersPerPixel) {
  return formatMeters(lengthPx * metersPerPixel);
}

/**
 * @param {number} meters
 * @returns {string}
 */
export function formatMetersInputValue(meters) {
  if (!Number.isFinite(meters) || meters <= 0) return '0.00';
  return meters.toFixed(2);
}

/**
 * @param {number} areaSqM
 * @returns {string}
 */
export function formatArea(areaSqM) {
  return `${areaSqM.toFixed(3)} m²`;
}

/**
 * @param {string} name
 * @param {number} areaSqM
 * @param {number} widthM
 * @param {number} depthM
 * @returns {string}
 */
export function formatRoomLabel(name, areaSqM, widthM, depthM) {
  return `${name}\n${areaSqM.toFixed(2)} m²\n${widthM.toFixed(2)} m × ${depthM.toFixed(2)} m`;
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {{ normalA: Point; normalB: Point; direction: Point; length: number }}
 */
export function getPerpendicularNormals(start, end) {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const len = Math.hypot(dx, dy) || 1;
  return {
    direction: [dx / len, dy / len],
    normalA: [-dy / len, dx / len],
    normalB: [dy / len, -dx / len],
    length: len,
  };
}

/**
 * @param {Point} point
 * @param {Point} normal
 * @param {number} distancePx
 * @returns {Point}
 */
export function offsetPointAlongNormal(point, normal, distancePx) {
  return [point[0] + normal[0] * distancePx, point[1] + normal[1] * distancePx];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @returns {Point}
 */
export function getWallMidpoint(start, end) {
  return [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
}

/**
 * @param {Point} point
 * @param {Array<Array<Point>>} roomPolygons
 * @returns {boolean}
 */
export function isPointInsideAnyRoom(point, roomPolygons) {
  for (const polygon of roomPolygons) {
    if (polygon.length >= 3 && isPointInPolygon(point, polygon)) {
      return true;
    }
  }
  return false;
}

/**
 * @param {{
 *   start: Point;
 *   end: Point;
 *   roomPolygons?: Array<Array<Point>>;
 *   testDistancePx?: number;
 *   displayScale?: number;
 * }} params
 * @returns {{
 *   interiorSign: 1 | -1;
 *   exteriorSign: -1 | 1;
 *   sideAIsInside: boolean;
 *   sideBIsInside: boolean;
 *   normalA: Point;
 *   normalB: Point;
 * }}
 */
export function resolveWallSideFromRooms({
  start,
  end,
  roomPolygons = [],
  testDistancePx = DIMENSION_SIDE_TEST_DISTANCE_PX,
  displayScale = 1,
}) {
  const { normalA, normalB } = getPerpendicularNormals(start, end);
  const safeScale = Math.max(displayScale || 1, 0.0001);
  const testDistance = testDistancePx / safeScale;
  const midpoint = getWallMidpoint(start, end);

  const testPointA = offsetPointAlongNormal(midpoint, normalA, testDistance);
  const testPointB = offsetPointAlongNormal(midpoint, normalB, testDistance);

  const sideAIsInside = isPointInsideAnyRoom(testPointA, roomPolygons);
  const sideBIsInside = isPointInsideAnyRoom(testPointB, roomPolygons);

  let interiorSign = /** @type {1 | -1} */ (1);
  let exteriorSign = /** @type {-1 | 1} */ (-1);

  if (sideAIsInside && !sideBIsInside) {
    interiorSign = 1;
    exteriorSign = -1;
  } else if (sideBIsInside && !sideAIsInside) {
    interiorSign = -1;
    exteriorSign = 1;
  } else if (sideAIsInside && sideBIsInside) {
    interiorSign = 1;
    exteriorSign = -1;
  } else {
    interiorSign = 1;
    exteriorSign = -1;
  }

  return {
    interiorSign,
    exteriorSign,
    sideAIsInside,
    sideBIsInside,
    normalA,
    normalB,
  };
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} offsetPx
 * @returns {{ labelPoint: Point; angleDeg: number; normal: Point }}
 */
export function dimensionLayout(start, end, offsetPx = 24) {
  const { normalA } = getPerpendicularNormals(start, end);
  const mid = getWallMidpoint(start, end);
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  return {
    labelPoint: [mid[0] + normalA[0] * offsetPx, mid[1] + normalA[1] * offsetPx],
    angleDeg: (Math.atan2(dy, dx) * 180) / Math.PI,
    normal: normalA,
  };
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} sideSign
 * @param {number} wallToArrowGapPx image-space gap from wall to arrow line
 * @returns {number[]}
 */
export function dimensionLinePointsForSide(start, end, sideSign, wallToArrowGapPx) {
  const { normalA } = getPerpendicularNormals(start, end);
  const magnitude = Math.abs(wallToArrowGapPx) * (sideSign < 0 ? -1 : 1);
  const ox = normalA[0] * magnitude;
  const oy = normalA[1] * magnitude;
  return [start[0] + ox, start[1] + oy, end[0] + ox, end[1] + oy];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} offsetPx
 * @returns {number[]}
 */
export function dimensionLinePoints(start, end, offsetPx = 20) {
  const magnitude = Math.abs(offsetPx);
  const sign = offsetPx < 0 ? -1 : 1;
  return dimensionLinePointsForSide(start, end, sign, magnitude);
}

/**
 * @param {string} text
 * @param {number} fontSize
 * @returns {number}
 */
export function estimateTextHalfWidth(text, fontSize) {
  return Math.max(fontSize * 0.55, (text.length * fontSize * 0.55) / 2);
}

/**
 * @typedef {Object} BuildDimensionAnnotationOptions
 * @property {number} [sideSign]
 * @property {number} [wallToArrowGapPx]
 * @property {number} [arrowToTextGapPx]
 * @property {number} [fontSizeBase]
 */

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} lengthPx
 * @param {number} metersPerPixel
 * @param {number} displayScale
 * @param {number | BuildDimensionAnnotationOptions} [lineOffsetOrOptions]
 * @param {number} [fontSizeBaseLegacy]
 * @returns {{
 *   linePoints: number[];
 *   textX: number;
 *   textY: number;
 *   textRotation: number;
 *   fontSize: number;
 *   pointerSize: number;
 *   strokeWidth: number;
 *   textOffsetX: number;
 *   textOffsetY: number;
 *   sideSign: number;
 * }}
 */
export function buildDimensionAnnotation(
  start,
  end,
  lengthPx,
  metersPerPixel,
  displayScale,
  lineOffsetOrOptions = {},
  fontSizeBaseLegacy = 11,
) {
  const safeScale = Math.max(displayScale || 1, 0.0001);
  /** @type {BuildDimensionAnnotationOptions} */
  let options = {};
  if (typeof lineOffsetOrOptions === 'number') {
    options = {
      sideSign: lineOffsetOrOptions < 0 ? -1 : 1,
      wallToArrowGapPx: Math.abs(lineOffsetOrOptions),
      fontSizeBase: fontSizeBaseLegacy,
    };
  } else {
    options = lineOffsetOrOptions || {};
  }

  const sideSign = options.sideSign ?? 1;
  const wallToArrowGapPx =
    (options.wallToArrowGapPx ?? DIMENSION_WALL_TO_ARROW_GAP_PX) / safeScale;
  const arrowToTextGapPx =
    (options.arrowToTextGapPx ?? DIMENSION_ARROW_TO_TEXT_GAP_PX) / safeScale;
  const fontSizeBase = options.fontSizeBase ?? fontSizeBaseLegacy ?? DIMENSION_SCREEN_FONT_PX;

  const linePoints = dimensionLinePointsForSide(start, end, sideSign, wallToArrowGapPx);
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const angleRad = Math.atan2(dy, dx);
  let textRotation = (angleRad * 180) / Math.PI;
  if (textRotation > 90 || textRotation < -90) {
    textRotation += 180;
  }

  const lineMidX = (linePoints[0] + linePoints[2]) / 2;
  const lineMidY = (linePoints[1] + linePoints[3]) / 2;
  const { normalA } = getPerpendicularNormals(start, end);
  const textDistance = (Math.abs(wallToArrowGapPx) + arrowToTextGapPx) * (sideSign < 0 ? -1 : 1);
  const textX = getWallMidpoint(start, end)[0] + normalA[0] * textDistance;
  const textY = getWallMidpoint(start, end)[1] + normalA[1] * textDistance;
  void lineMidX;
  void lineMidY;

  const fontSize = fontSizeBase / safeScale;
  const labelText = formatLengthFromPixels(lengthPx, metersPerPixel);

  return {
    linePoints,
    textX,
    textY,
    textRotation,
    fontSize,
    pointerSize: DIMENSION_SCREEN_ARROW_PX / safeScale,
    strokeWidth: DIMENSION_SCREEN_STROKE_PX / safeScale,
    textOffsetX: estimateTextHalfWidth(labelText, fontSize),
    textOffsetY: fontSize / 2,
    sideSign,
  };
}

/**
 * @param {[Point, Point, Point, Point]} wallPolygon
 * @param {number} metersPerPixel
 * @param {{
 *   roomPolygons?: Array<Array<Point>>;
 *   displayScale?: number;
 * }} [options]
 * @returns {Array<{
 *   start: Point;
 *   end: Point;
 *   lengthPx: number;
 *   sideSign: 1 | -1;
 *   type: 'pivot-edge' | 'thickness-edge';
 *   text: string;
 *   placementKind: 'exterior' | 'interior';
 * }>}
 */
export function buildWallEdgeDimensions(wallPolygon, metersPerPixel, options = {}) {
  if (!wallPolygon || wallPolygon.length !== 4) return [];

  const { roomPolygons = [], displayScale = 1 } = options;
  const [A, B, offsetB, offsetA] = wallPolygon;
  const pivotLen = Math.hypot(B[0] - A[0], B[1] - A[1]);
  const thicknessLen = Math.hypot(offsetB[0] - offsetA[0], offsetB[1] - offsetA[1]);
  const pivotText = formatLengthFromPixels(pivotLen, metersPerPixel);
  const thicknessText = formatLengthFromPixels(thicknessLen, metersPerPixel);

  const pivotSides = resolveWallSideFromRooms({
    start: A,
    end: B,
    roomPolygons,
    displayScale,
  });
  const thicknessSides = resolveWallSideFromRooms({
    start: offsetA,
    end: offsetB,
    roomPolygons,
    displayScale,
  });

  return [
    {
      start: A,
      end: B,
      lengthPx: pivotLen,
      sideSign: pivotSides.exteriorSign,
      type: 'pivot-edge',
      text: pivotText,
      placementKind: 'exterior',
    },
    {
      start: offsetA,
      end: offsetB,
      lengthPx: thicknessLen,
      sideSign: thicknessSides.interiorSign,
      type: 'thickness-edge',
      text: thicknessText,
      placementKind: 'interior',
    },
  ];
}

/**
 * @param {Point} start
 * @param {Point} end
 * @param {number} metersPerPixel
 * @param {{
 *   roomPolygons?: Array<Array<Point>>;
 *   displayScale?: number;
 * }} [options]
 * @returns {{
 *   start: Point;
 *   end: Point;
 *   lengthPx: number;
 *   sideSign: 1 | -1;
 *   type: 'centerline';
 *   text: string;
 *   placementKind: 'exterior' | 'interior';
 * } | null}
 */
export function buildCenterlineWallDimension(start, end, metersPerPixel, options = {}) {
  if (!start || !end) return null;
  const lengthPx = Math.hypot(end[0] - start[0], end[1] - start[1]);
  if (lengthPx <= 0) return null;
  const { roomPolygons = [], displayScale = 1 } = options;
  const sides = resolveWallSideFromRooms({ start, end, roomPolygons, displayScale });
  return {
    start,
    end,
    lengthPx,
    sideSign: sides.exteriorSign,
    type: 'centerline',
    text: formatLengthFromPixels(lengthPx, metersPerPixel),
    placementKind: 'exterior',
  };
}
